import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiSettings, FiTrendingUp, FiUserPlus } from "react-icons/fi";
import { FaRegComment, FaRegBookmark, FaInfoCircle, FaUser } from "react-icons/fa";
import { TrendingUp, Trophy, Flame } from "lucide-react";
import clsx from "clsx";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import type { ProfileTabId } from "./components/profile.types";
import { UserEditProfileModal } from "./components/EditProfileModal";
import { PuzzleActivityCard } from "./components/PuzzleActivityCard";
import { useUser } from "@/context/UserContext";
import { determineTileMode } from "@/helpers/puzzleShare";
import {
    GUEST_PROFILE_VIEWER_NICKNAME,
    findUserNotificationSetting,
    getUserProfileActivity,
    getUserProfileHeader,
    getUserProfileStats,
    uploadUserProfileImage,
    updateUserProfile,
    type UpdateUserProfilePayload,
} from "@/services/UserService";

type TabMeta = {
    id: ProfileTabId;
    label: string;
    Icon: React.ComponentType<{ className?: string }>;
};

const ACTIVITY_TABS: TabMeta[] = [
    { id: "listings", label: "Activity", Icon: FiTrendingUp },
    { id: "comments", label: "Comments", Icon: FaRegComment },
    { id: "saved", label: "Saved", Icon: FaRegBookmark },
];

const ABOUT_TAB: TabMeta = { id: "about", label: "About", Icon: FaInfoCircle };

type TileColor = "correct" | "near" | "gray";

function formatLocationLabel(city?: string | null, state?: string | null): string {
    return [city, state].filter((part) => !!part && String(part).trim().length > 0).join(", ");
}

interface UserProfileProps {
    nickname: string;
    onBack: () => void;
}

export const UserProfile: React.FC<UserProfileProps> = ({ nickname, onBack }) => {
    const { userState, fetchUserData, setUserState } = useUser();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const currentNickname = userState?.nickname ?? undefined;
    const profileNickname = (nickname ?? "").trim();
    const isOwnProfile = !!currentNickname && profileNickname.toLowerCase() === currentNickname.toLowerCase();

    /** Logged-in viewer: real nickname (covers own-profile bypass and viewerHasPlayed on others). Anonymous: guest placeholder. */
    const activityViewerNickname = currentNickname?.trim() || GUEST_PROFILE_VIEWER_NICKNAME;

    const {
        data: profileHeader,
        isLoading: isHeaderLoading,
        isError: isHeaderError,
    } = useQuery({
        queryKey: ["user-profile-header", profileNickname],
        queryFn: () => getUserProfileHeader(profileNickname),
        enabled: !!profileNickname,
        retry: false,
    });

    const { data: profileNotificationSettings } = useQuery({
        queryKey: ["user-profile-notification-settings", userState?.id],
        queryFn: () => findUserNotificationSetting(String(userState?.id)),
        enabled: !!isOwnProfile && !!userState?.id,
        retry: false,
    });

    const canViewProfileContent = isOwnProfile || !profileHeader?.profilePrivate;
    const shouldFetchRestrictedSections = !!profileHeader && canViewProfileContent;

    const {
        data: profileStats,
        isLoading: isStatsLoading,
        isError: isStatsError,
    } = useQuery({
        queryKey: ["user-profile-stats", profileNickname],
        queryFn: () => getUserProfileStats(profileNickname),
        enabled: !!profileNickname && shouldFetchRestrictedSections,
        retry: false,
    });

    const {
        data: profileActivityPages,
        isLoading: isActivityInitialLoading,
        isError: isActivityError,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ["user-profile-activity", profileNickname, activityViewerNickname ?? "own"],
        queryFn: ({ pageParam }) =>
            getUserProfileActivity(profileNickname, {
                page: Number(pageParam ?? 1),
                limit: 12,
                ...(activityViewerNickname !== undefined ? { viewerNickname: activityViewerNickname } : {}),
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => (lastPage.hasNextPage ? (lastPage.nextPage ?? undefined) : undefined),
        enabled: !!profileNickname && shouldFetchRestrictedSections,
        retry: false,
    });

    const displayNameFromState = `${userState?.first_name ?? ""} ${userState?.last_name ?? ""}`.trim();

    const user = {
        displayName: profileHeader?.displayName?.trim() || displayNameFromState || profileNickname || "Player",
        username: profileHeader?.nickname || profileNickname,
        avatar: profileHeader?.profilePhotoUrl || null,
        bio: profileHeader?.bio ?? "",
        stats: {
            totalSolved: profileStats?.solved ?? 0,
            avgScore: profileStats?.averageScore ?? null,
            streak: profileStats?.currentStreak ?? 0,
            followers: 0,
            following: 0,
        },
    };

    const [activeTab, setActiveTab] = useState<ProfileTabId>("listings");
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [followState, setFollowState] = useState<"follow" | "following">("follow");

    const isLoadingProfile =
        isHeaderLoading || (shouldFetchRestrictedSections && (isStatsLoading || isActivityInitialLoading));
    const isProfileError =
        isHeaderError ||
        (shouldFetchRestrictedSections &&
            ((isStatsError && !profileStats) || (isActivityError && !profileActivityPages)));
    const isPrivateOther = !!profileHeader?.profilePrivate && !isOwnProfile;
    const activityItems = useMemo(
        () => profileActivityPages?.pages.flatMap((page) => page.data ?? []) ?? [],
        [profileActivityPages],
    );
    const loadMoreRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const target = loadMoreRef.current;
        if (!target || !hasNextPage) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            { rootMargin: "300px" },
        );

        observer.observe(target);
        return () => observer.disconnect();
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    if (isLoadingProfile && !profileHeader && !profileStats) {
        return (
            <div className="tw-max-w-[600px] tw-mx-auto tw-min-h-[calc(100vh-9svh)] tw-flex tw-items-center tw-justify-center tw-text-sm tw-text-[var(--dark-grey)]">
                Loading profile…
            </div>
        );
    }

    if (isProfileError && !profileHeader) {
        return (
            <div className="tw-max-w-[600px] tw-mx-auto tw-min-h-[calc(100vh-9svh)] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-text-sm md:tw-text-base tw-text-[var(--dark-grey)] tw-px-6 tw-gap-3">
                <div>
                    <p className="tw-font-semibold tw-mb-1">User profile not found</p>
                    <p className="tw-m-0 tw-text-[13px] md:tw-text-sm tw-text-[var(--nobel-gray)]">
                        We couldn&apos;t load this user&apos;s profile. It may be private, deleted, or the link might
                        be incorrect.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onBack}
                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-[var(--orange)] tw-bg-white tw-text-[var(--orange)] tw-text-xs md:tw-text-sm tw-px-4 tw-py-1.5 tw-font-semibold tw-transition-colors hover:tw-bg-[var(--orange)] hover:tw-text-white"
                >
                    Go back
                </button>
            </div>
        );
    }

    const toGuessAttempts = (
        guesses: number[],
        actualPrice: number | undefined,
    ): { digits: number[]; colors: TileColor[] }[] =>
        (guesses || []).map((guess) => {
            const digits = String(Math.round(guess)).padStart(8, "0").split("").map((d) => Number(d));
            const actualDigits =
                typeof actualPrice === "number" && !Number.isNaN(actualPrice)
                    ? String(Math.round(actualPrice))
                          .padStart(8, "0")
                          .split("")
                          .map(Number)
                    : null;
            const colors: TileColor[] =
                actualDigits
                    ? digits.map((guessDigit, index) => {
                          const mode = determineTileMode(actualDigits[index] ?? 0, guessDigit);
                          if (mode === "correct") return "correct";
                          if (mode === "close") return "near";
                          return "gray";
                      })
                    : Array.from({ length: 8 }, () => "gray");
            return { digits, colors };
        });

    const handleSaveProfile = async (payload: UpdateUserProfilePayload, profileImageFile?: File | null) => {
        if (!userState?.id) {
            throw new Error("Missing user id");
        }
        const updatedUser = await updateUserProfile(userState.id, payload);
        const updatedNickname = updatedUser?.nickname?.trim();
        const nextShowScoreLeaderboard =
            typeof payload.showScoreLeaderboard === "boolean"
                ? payload.showScoreLeaderboard
                : updatedUser?.showScoreLeaderboard;
        const nextProfileNickname = updatedNickname || profileNickname;

        const nextUserProps: Partial<typeof userState> = {};
        if (updatedUser?.nickname && updatedUser.nickname !== userState.nickname) {
            nextUserProps.nickname = updatedUser.nickname;
        }
        if (
            typeof nextShowScoreLeaderboard === "boolean" &&
            nextShowScoreLeaderboard !== userState.showScoreLeaderboard
        ) {
            nextUserProps.showScoreLeaderboard = nextShowScoreLeaderboard;
        }

        if (Object.keys(nextUserProps).length > 0) {
            setUserState(userState, userState.is_created, nextUserProps);
        }
        queryClient.setQueryData(["user-profile-header", nextProfileNickname], (prev: any) =>
            prev
                ? {
                      ...prev,
                      profilePrivate: updatedUser?.profilePrivate ?? prev.profilePrivate,
                      displayName: updatedUser?.displayName ?? prev.displayName,
                      nickname: updatedUser?.nickname ?? prev.nickname,
                      bio: updatedUser?.bio ?? prev.bio,
                      profilePhotoUrl: updatedUser?.profilePhotoUrl ?? prev.profilePhotoUrl,
                      showScoreLeaderboard: nextShowScoreLeaderboard ?? prev.showScoreLeaderboard,
                  }
                : prev,
        );
        if (profileImageFile) {
            await uploadUserProfileImage(userState.id, profileImageFile);
            await fetchUserData(userState.id);
        }
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["user-profile-header", nextProfileNickname] }),
            queryClient.invalidateQueries({ queryKey: ["user-profile-stats", nextProfileNickname] }),
            queryClient.invalidateQueries({
                queryKey: ["user-profile-activity", nextProfileNickname, activityViewerNickname ?? "own"],
            }),
            // Invalidate all viewer variants for this profile; privacy toggles affect visibility.
            queryClient.invalidateQueries({
                queryKey: ["user-profile-activity", nextProfileNickname],
            }),
            queryClient.invalidateQueries({
                queryKey: ["user-profile-notification-settings", userState?.id],
            }),
        ]);

        if (updatedNickname && updatedNickname.toLowerCase() !== profileNickname.toLowerCase()) {
            navigate(`/profile/${encodeURIComponent(updatedNickname)}`, { replace: true });
        }
    };

    return (
        <>
            <div
                className="tw-max-w-[600px] md:tw-max-w-[1200px] tw-mx-auto tw-text-white tw-px-6 tw-pt-4 md:tw-pt-6 tw-pb-2 tw-flex tw-flex-col tw-items-center tw-text-center tw-bg-[linear-gradient(175deg,_rgb(61,24,115)_0%,_rgb(42,16,82)_40%,_rgb(26,10,53)_100%)]"
            >
                <div className="tw-w-full tw-max-w-[600px] md:tw-max-w-[1200px] tw-flex tw-flex-col tw-gap-3">
                    <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-center md:tw-items-start tw-justify-between tw-gap-4">
                        <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-center md:tw-items-start tw-gap-3 tw-w-full md:tw-w-auto">
                            <div className="tw-relative tw-m-2">
                                {user.avatar ? (
                                    <img
                                        src={user.avatar}
                                        alt={user.displayName}
                                        className="tw-w-[82px] tw-h-[82px] tw-rounded-full tw-object-cover tw-border-2 tw-border-[var(--orange)]"
                                    />
                                ) : (
                                    <div className="tw-w-[82px] tw-h-[82px] tw-rounded-full tw-border-2 tw-border-[var(--orange)] tw-bg-white/10 tw-flex tw-items-center tw-justify-center">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-white/90">
                                            <FaUser className="tw-text-[26px]" />
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="tw-text-center md:tw-text-left">
                                <h1 className="tw-font-['Merriweather',serif] tw-font-bold tw-text-xl tw-m-0 tw-p-0 tw-mb-[2px]">
                                    {user.displayName}
                                </h1>
                                <p className="tw-text-sm md:tw-text-base tw-text-white/50 tw-mb-[1px]">
                                    @{user.username}
                                </p>
                            </div>
                        </div>
                        <div className="tw-w-full md:tw-w-auto tw-flex tw-justify-center md:tw-justify-end">
                            {isOwnProfile ? (
                                <button
                                    type="button"
                                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg !tw-border !tw-border-solid !tw-border-white/30 tw-bg-white/10 tw-text-xs md:tw-text-sm tw-px-4 tw-py-1.5 tw-font-semibold tw-transition-colors hover:tw-bg-white/20"
                                    onClick={() => setIsEditingProfile(true)}
                                >
                                    <FiSettings className="tw-text-[15px]" />
                                    Edit Profile
                                </button>
                            ) : (
                                // TODO(PM-Profile): Re-enable follow button once follow feature is ready.
                                <button
                                    type="button"
                                    className={clsx(
                                        "tw-hidden tw-items-center tw-gap-1.5 tw-rounded-full tw-px-4 tw-py-1.5 tw-font-semibold tw-text-xs md:tw-text-sm tw-transition-colors tw-border",
                                        followState === "follow"
                                            ? "tw-bg-[var(--orange)] tw-border-[var(--orange)] tw-text-white hover:tw-bg-[#ffb84a]"
                                            : "tw-bg-white/10 tw-border-white/40 tw-text-white hover:tw-bg-white/20",
                                    )}
                                    onClick={() =>
                                        setFollowState((prev) => (prev === "follow" ? "following" : "follow"))
                                    }
                                >
                                    <FiUserPlus className="tw-text-[13px]" />
                                    {followState === "follow" ? "Follow" : "Following"}
                                </button>
                            )}
                        </div>
                    </div>

                    <p className="tw-text-sm md:tw-text-[15px] tw-leading-relaxed tw-max-w-[26rem] tw-mb-0 tw-text-center md:tw-text-left">
                        {user.bio}
                    </p>
                </div>
                <hr className="tw-w-full tw-border-t tw-border-white/20 tw-mt-2 tw-mb-4" />
                {/* Desktop: stats on left, social+contact on right in a single row. Mobile: stacked. */}
                <div className="tw-flex tw-w-full tw-flex-col md:tw-flex-row tw-items-center md:tw-items-center tw-justify-center md:tw-justify-between tw-gap-3 md:tw-gap-6 tw-mb-3 md:tw-mb-4 tw-text-xs md:tw-text-sm">
                    {/* Solved / avg score / streak */}
                    <div className="tw-flex tw-flex-wrap tw-justify-center md:tw-justify-start tw-gap-2">
                        <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white/10 tw-px-3 tw-py-1.5">
                            <TrendingUp className="tw-w-3 tw-h-3 tw-text-[#ffb84d]" />
                            <span className="tw-text-white/70 tw-text-[11px] md:tw-text-xs">
                                <span className="tw-font-semibold tw-text-white">
                                    {user.stats.totalSolved.toLocaleString()}
                                </span>{" "}
                                solved
                            </span>
                        </div>
                        <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white/10 tw-px-3 tw-py-1.5">
                            <Trophy className="tw-w-3 tw-h-3 tw-text-[#ffb84d]" />
                            <span className="tw-text-white/70 tw-text-[11px] md:tw-text-xs">
                                <span className="tw-font-semibold tw-text-white">
                                    {user.stats.avgScore == null ? "—" : user.stats.avgScore.toFixed(1)}
                                </span>{" "}
                                avg score
                            </span>
                        </div>
                        <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white/10 tw-px-3 tw-py-1.5">
                            <Flame className="tw-w-3 tw-h-3 tw-text-[#ffb84d]" />
                            <span className="tw-text-white/70 tw-text-[11px] md:tw-text-xs">
                                <span className="tw-font-semibold tw-text-white">{user.stats.streak}</span> day streak
                            </span>
                        </div>
                    </div>
                    {/* TODO(PM-Profile): Re-enable followers/following counts when social metrics are finalized. */}
                    <div className="tw-hidden tw-flex-col tw-items-center md:tw-items-end tw-gap-0.5 tw-text-xs md:tw-text-sm">
                        <span className="tw-text-white/50 tw-font-semibold">
                            {user.stats.followers.toLocaleString()} followers
                        </span>
                        <span className="tw-text-white/50 tw-text-[11px]">
                            Following {user.stats.following.toLocaleString()}
                        </span>
                    </div>
                </div>
            </div>

            {/* Sticky tabs - hidden for other user's private profile */}
            {!isPrivateOther && (
                <div className="tw-sticky tw-top-[9svh] tw-z-40 tw-bg-white tw-border-b tw-border-[var(--grey)]">
                    <div className="tw-flex tw-max-w-[600px] md:tw-max-w-[1200px] tw-mx-auto">
                        {[...ACTIVITY_TABS, ABOUT_TAB].map((tab) => {
                            const isActive = activeTab === tab.id;
                            const isDisabled = tab.id !== "listings";
                            const baseClasses =
                                "tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-py-3 tw-text-[11px] md:tw-text-xs tw-transition-colors tw-bg-white";
                            const activeClasses = "tw-text-[var(--orange)] tw-font-semibold";
                            const inactiveClasses = "tw-text-[var(--nobel-gray)]";
                            const Icon = tab.Icon;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    className={clsx(
                                        baseClasses,
                                        isActive ? activeClasses : inactiveClasses,
                                        isDisabled && "tw-opacity-50 tw-cursor-not-allowed",
                                    )}
                                    disabled={isDisabled}
                                    onClick={() => {
                                        if (isDisabled) return;
                                        setActiveTab(tab.id);
                                    }}
                                >
                                    <Icon className="tw-text-[16px]" />
                                    <span>{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>
                    {/* Active tab underline indicator */}
                    {(() => {
                        const tabs = [...ACTIVITY_TABS, ABOUT_TAB];
                        const activeIndex = tabs.findIndex((tab) => tab.id === activeTab);
                        if (activeIndex === -1) return null;
                        const widthPercent = 100 / tabs.length;
                        return (
                            <div
                                className="tw-absolute tw-bottom-0 tw-h-[2px] tw-bg-[var(--orange)] tw-transition-all tw-duration-200"
                                style={{
                                    width: `${widthPercent}%`,
                                    left: `${widthPercent * activeIndex}%`,
                                }}
                            />
                        );
                    })()}
                </div>
            )}

            {/* Content */}
            <div className="tw-bg-white tw-rounded-b-3xl tw-shadow-md tw-px-3 sm:tw-px-4 tw-py-5 tw-min-h-[200px] tw-w-full tw-mx-auto">
                {isPrivateOther ? (
                    <p className="tw-text-center tw-text-sm md:tw-text-base tw-text-[var(--dark-grey)] tw-py-8">
                        This user&apos;s profile is private.
                    </p>
                ) : (
                    activeTab === "listings" && (
                    <>
                        {!isActivityInitialLoading && activityItems.length === 0 ? (
                            <div className="tw-text-center tw-text-sm md:tw-text-base tw-text-[var(--dark-grey)] tw-py-10">
                                There&apos;s nothing here…yet.{" "}
                                <a href="/puzzle" className="tw-text-[var(--orange)] tw-font-semibold hover:tw-underline">
                                    Take your first guess.
                                </a>
                            </div>
                        ) : (
                            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-4 tw-w-full">
                                {Array.from({
                                    length: Math.max(activityItems.length, 3),
                                }).map((_, index) => {
                                    const listing = activityItems[index];

                                    // If there are < 3 listings, render invisible placeholders on md+ so the grid
                                    // still behaves like a full 3-column layout (no "shrinking" layout).
                                    if (!listing) {
                                        return (
                                            <div
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`listing-placeholder-${index}`}
                                                className="tw-hidden md:tw-block tw-invisible"
                                                aria-hidden="true"
                                            >
                                                <div className="tw-relative tw-w-full tw-aspect-[16/11] tw-overflow-hidden" />
                                            </div>
                                        );
                                    }

                                    const locationLabel = formatLocationLabel(listing.city, listing.state);
                                    const viewerHasPlayed =
                                        typeof listing.viewerHasPlayed === "boolean"
                                            ? listing.viewerHasPlayed
                                            : isOwnProfile;
                                    const fallbackActualPrice = listing.actualPrice ?? undefined;

                                    return (
                                        <PuzzleActivityCard
                                            key={listing.playablePropertyId}
                                            image={listing.propertyImage || "/placeholder-property.jpg"}
                                            address={listing.streetAddress ?? ""}
                                            locationLabel={locationLabel}
                                            beds={listing.beds ?? undefined}
                                            baths={listing.baths ?? undefined}
                                            sqft={listing.sqft ?? undefined}
                                            datePlayedLabel={`Played ${new Date(listing.datePlayed).toLocaleDateString()}`}
                                            puzzleHref={`/puzzle/${listing.shortLink ?? listing.playablePropertyId}`}
                                            guessAttempts={toGuessAttempts(
                                                listing.guesses || [],
                                                fallbackActualPrice,
                                            )}
                                            viewerHasPlayed={viewerHasPlayed}
                                            otherUserNickname={!isOwnProfile ? profileNickname : undefined}
                                            profileViewed={isOwnProfile ? "own" : "other"}
                                        />
                                    );
                                })}
                            </div>
                        )}
                        <div ref={loadMoreRef} className="tw-h-1 tw-w-full" aria-hidden="true" />
                        {isFetchingNextPage && (
                            <p className="tw-text-center tw-text-sm tw-text-[var(--dark-grey)] tw-pt-4">Loading more...</p>
                        )}
                        {hasNextPage && (
                            <div className="tw-flex tw-justify-center tw-mt-4">
                                <button
                                    type="button"
                                    onClick={() => fetchNextPage()}
                                    disabled={isFetchingNextPage}
                                    className="tw-bg-purple-600 hover:tw-bg-purple-700 tw-text-white tw-px-6 tw-py-2 tw-rounded-lg tw-font-medium tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                >
                                    {isFetchingNextPage ? "Loading..." : "Load More"}
                                </button>
                            </div>
                        )}
                    </>
                    )
                )}
                {!isPrivateOther && activeTab !== "listings" && (
                    <p className="tw-text-center tw-text-sm md:tw-text-base tw-text-[var(--dark-grey)] tw-py-8 tw-opacity-60">
                        This section is coming soon.
                    </p>
                )}
            </div>

            {/* Edit Profile - modal on desktop, drawer on mobile */}
            {isOwnProfile && (
                <UserEditProfileModal
                    open={isEditingProfile}
                    onClose={() => setIsEditingProfile(false)}
                    onSave={handleSaveProfile}
                        profile={{
                            name: user.displayName,
                            emailAddress: userState?.emailAddress ?? undefined,
                            handle: `@${user.username}`,
                            company: undefined,
                            bio: user.bio,
                            phone: undefined,
                            websiteUrl: undefined,
                            avatarUrl: user.avatar ?? undefined,
                            socialLinks: {
                                instagram: undefined,
                                tiktok: undefined,
                                twitter: undefined,
                                facebook: undefined,
                                youtube: undefined,
                            },
                            showScoreLeaderboard: userState?.showScoreLeaderboard ?? profileHeader?.showScoreLeaderboard,
                            profilePrivate: profileHeader?.profilePrivate,
                            notificationSettings: profileNotificationSettings,
                        }}
                />
            )}
        </>
    );
};

